import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, Plus, Trash2, KeyRound, UserPlus, Shield, Calendar } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import api from '../api/axios';

const ROLE_LABELS = {
  webmaster: 'Webmaster',
  admin: 'Admin',
  serveur: 'Serveur/se',
  caissier: 'Caissier/e',
  cuisinier: 'Cuisinier/e'
};

const ROLE_COLORS = {
  webmaster: 'bg-purple-100 text-purple-800',
  admin: 'bg-red-100 text-red-800',
  serveur: 'bg-blue-100 text-blue-800',
  caissier: 'bg-green-100 text-green-800',
  cuisinier: 'bg-amber-100 text-amber-800'
};

const UsersManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showPassword, setShowPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [form, setForm] = useState({ username: '', password: '', nom: '', prenom: '', role: 'serveur', habilitation_end: '' });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch (e) { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  const handleCreate = async () => {
    if (!form.username || !form.password || !form.nom || !form.prenom) {
      toast({ title: 'Remplissez tous les champs', variant: 'destructive' }); return;
    }
    if (form.password.length !== 6 || !/^\d{6}$/.test(form.password)) {
      toast({ title: 'Le code doit etre exactement 6 chiffres', variant: 'destructive' }); return;
    }
    try {
      await api.post('/users', { ...form, habilitation_end: form.habilitation_end || null });
      toast({ title: 'Utilisateur cree' });
      setShowCreate(false);
      setForm({ username: '', password: '', nom: '', prenom: '', role: 'serveur', habilitation_end: '' });
      fetchUsers();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur creation', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await api.delete(`/users/${id}`);
      toast({ title: 'Utilisateur supprime' });
      fetchUsers();
    } catch (e) { toast({ title: 'Erreur', description: e.response?.data?.detail, variant: 'destructive' }); }
  };

  const handleToggleActive = async (user) => {
    try {
      await api.put(`/users/${user.id}`, { active: !user.active });
      toast({ title: user.active ? 'Desactive' : 'Active' });
      fetchUsers();
    } catch (e) { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length !== 6 || !/^\d{6}$/.test(newPassword)) {
      toast({ title: 'Le code doit etre 6 chiffres', variant: 'destructive' }); return;
    }
    try {
      await api.put(`/users/${showPassword}/password`, { new_password: newPassword });
      toast({ title: 'Code modifie' });
      setShowPassword(null);
      setNewPassword('');
    } catch (e) { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white">
      <div className="bg-[#3d2817] text-white py-3 shadow-lg">
        <div className="max-w-5xl mx-auto px-4 flex items-center gap-4">
          <Button variant="ghost" className="text-white hover:bg-white/10" onClick={() => navigate('/staff/dashboard')} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <Shield size={22} />
          <h1 className="text-lg font-bold">Gestion des Utilisateurs</h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <Button onClick={() => setShowCreate(true)} className="mb-6 bg-[#8B0000] hover:bg-[#6d0000]" data-testid="create-user-btn">
          <UserPlus size={18} className="mr-2" /> Nouvel Utilisateur
        </Button>

        <div className="grid gap-3">
          {users.map(user => (
            <Card key={user.id} className={`${!user.active ? 'opacity-50' : ''}`} data-testid={`user-${user.username}`}>
              <CardContent className="py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <p className="font-bold text-sm">{user.prenom} {user.nom}</p>
                    <p className="text-xs text-gray-500">@{user.username}</p>
                    {user.habilitation_end && (
                      <p className="text-xs text-orange-600 flex items-center gap-1 mt-1">
                        <Calendar size={12} /> Expire: {new Date(user.habilitation_end).toLocaleDateString('fr-FR', { timeZone: 'America/Cayenne' })}
                      </p>
                    )}
                  </div>
                  <Badge className={ROLE_COLORS[user.role] || 'bg-gray-100'}>{ROLE_LABELS[user.role] || user.role}</Badge>
                  {!user.active && <Badge variant="outline" className="text-red-500 border-red-300">Inactif</Badge>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleToggleActive(user)} data-testid={`toggle-${user.username}`}>
                    {user.active ? 'Desactiver' : 'Activer'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setShowPassword(user.id); setNewPassword(''); }} data-testid={`pwd-${user.username}`}>
                    <KeyRound size={14} />
                  </Button>
                  {user.role !== 'webmaster' && (
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(user.id)} data-testid={`del-${user.username}`}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Create User Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md" data-testid="create-user-modal">
          <DialogHeader><DialogTitle>Nouvel Utilisateur</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Prenom</Label>
                <Input value={form.prenom} onChange={e => setForm({ ...form, prenom: e.target.value })} placeholder="Jean" data-testid="input-prenom" />
              </div>
              <div>
                <Label>Nom</Label>
                <Input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} placeholder="Dupont" data-testid="input-nom" />
              </div>
            </div>
            <div>
              <Label>Nom d'utilisateur</Label>
              <Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '') })} placeholder="jean.dupont" data-testid="input-username" />
            </div>
            <div>
              <Label>Code d'acces (6 chiffres)</Label>
              <Input type="password" inputMode="numeric" maxLength={6}
                value={form.password}
                onChange={e => { const v = e.target.value.replace(/\D/g, ''); setForm({ ...form, password: v }); }}
                placeholder="000000" className="tracking-[0.3em] text-center text-lg"
                data-testid="input-password" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="select-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="serveur">Serveur/se</SelectItem>
                  <SelectItem value="caissier">Caissier/e</SelectItem>
                  <SelectItem value="cuisinier">Cuisinier/e</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fin d'habilitation (optionnel)</Label>
              <Input type="date" value={form.habilitation_end}
                onChange={e => setForm({ ...form, habilitation_end: e.target.value })}
                data-testid="input-habilitation" />
              <p className="text-xs text-gray-500 mt-1">Pour les employes temporaires</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={handleCreate} className="bg-[#8B0000] hover:bg-[#6d0000]" data-testid="confirm-create-btn">Creer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={!!showPassword} onOpenChange={o => { if (!o) setShowPassword(null); }}>
        <DialogContent className="sm:max-w-sm" data-testid="change-pwd-modal">
          <DialogHeader><DialogTitle>Changer le code</DialogTitle></DialogHeader>
          <div className="py-2">
            <Label>Nouveau code (6 chiffres)</Label>
            <Input type="password" inputMode="numeric" maxLength={6}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value.replace(/\D/g, ''))}
              placeholder="000000" className="tracking-[0.3em] text-center text-lg mt-2"
              data-testid="new-password-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPassword(null)}>Annuler</Button>
            <Button onClick={handleChangePassword} className="bg-[#8B0000] hover:bg-[#6d0000]" data-testid="confirm-pwd-btn">Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersManagement;
